import {
    GetSavingsReportsParams,
    type SavingsReport,
    formatNumber as fmtNum,
    formatMonthShort,
    getRange,
    monthNames,
} from "@pentacode/core";
import type { ChartDefinition, ChartTooltipBodyRenderContext } from "@pentacode/ui/charts";
import {
    barChart,
    formatCurrency,
    formatPercent,
    returnChart,
    savingsChart,
    savingsColors,
} from "@pentacode/ui/charts";
import "@pentacode/ui/components";
import type { ChartPointEventDetail } from "@pentacode/ui/components";
import { LitElement, css, html } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { alert } from "../../elements/alert-dialog";
import { app } from "../../init";
import { Routing, routeProperty } from "../../mixins/routing";
import { StateMixin } from "../../mixins/state";
import { shared } from "../../styles";
import { savingsDays } from "./savings-days";

const formatNumber = (val: number) => fmtNum(val, 0);

@customElement("ptc-reports-savings")
export class Reports extends Routing(StateMixin(LitElement)) {
    routePattern = /^reports\/savings/;

    get routeTitle() {
        return "Ersparnisbericht";
    }

    @state()
    private _loading = false;

    @state()
    private _dailyReports: SavingsReport[] = [];

    @state()
    private _monthlyReports: SavingsReport[] = [];

    @routeProperty({ param: "year", type: Number, default: () => new Date().getFullYear() })
    private _year: number;

    @routeProperty({ param: "month", type: Number, default: () => new Date().getMonth() })
    private _month: number;

    @state()
    private _chartSavingsMonthly?: ChartDefinition;

    @state()
    private _chartSavingsDaily?: ChartDefinition;

    @state()
    private _chartSavingsReturn?: ChartDefinition;

    @state()
    private _monthlyShares = new Map<string, number>();

    @state()
    private _monthTotals = { savings: 0, fees: 0 };

    @state()
    private _highlight?: string;

    @query("#yearSelect")
    private _yearSelect: HTMLSelectElement;

    @query("#monthSelect")
    private _monthSelect: HTMLSelectElement;

    willUpdate(changes: Map<string, unknown>) {
        if (changes.has("_monthlyReports") || changes.has("_highlight")) {
            this._updateMonthlyCharts();
        }
        if (changes.has("_dailyReports")) {
            this._updateSavingsReturn();
        }
        if (changes.has("_dailyReports") || changes.has("_highlight")) {
            this._updateDailyCharts();
        }
    }

    async handleRoute() {
        void this._load();
        await this.updateComplete;
        this._yearSelect.value = this._year.toString();
        this._monthSelect.value = this._month.toString();
    }

    private _yearSelected(e: Event) {
        const year = Number((e.target as HTMLSelectElement).value);
        this.go(null, { month: this._month, year });
    }

    private _monthSelected(e: Event) {
        const month = Number((e.target as HTMLSelectElement).value);
        this.go(null, { month, year: this._year });
    }

    private async _load() {
        if (!this._year) {
            return;
        }

        const currYear = getRange(new Date(this._year, 0, 1), "year");
        const currMonth = getRange(new Date(this._year, this._month, 1), "month");

        this._loading = true;

        try {
            const [monthlyReports, dailyReports] = await Promise.all([
                app.api.getSavingsReports(new GetSavingsReportsParams({ ...currYear, resolution: "month" })),
                app.api.getSavingsReports(new GetSavingsReportsParams({ ...currMonth, resolution: "day" })),
            ]);
            this._monthlyReports = monthlyReports;
            this._dailyReports = dailyReports;
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        this._loading = false;
    }

    private _monthLabel(report: SavingsReport) {
        return formatMonthShort(report.from, false);
    }

    private _updateMonthlyCharts() {
        const savings = this._monthlyReports.map((r) => ({
            x: this._monthLabel(r),
            series: "Ersparnis",
            value: r.savings,
        }));
        const fees = this._monthlyReports.map((r) => ({ x: this._monthLabel(r), series: "Gebühren", value: r.fees }));

        this._monthlyShares = new Map(
            this._monthlyReports.flatMap((r) =>
                r.actualCosts ? [[this._monthLabel(r), r.savings / r.actualCosts] as const] : []
            )
        );
        this._chartSavingsMonthly = barChart(savings, {
            yAxis: "right",
            line: fees,
            legend: false,
            highlight: this._highlight,
            format: formatCurrency,
            colors: savingsColors,
        });
    }

    private _renderMonthlyTooltip = ({ defaultBody, points }: ChartTooltipBodyRenderContext) => {
        const share = this._monthlyShares.get(String(points[0]?.xValue));
        return html`${defaultBody}
        ${share === undefined ? "" : html`<div style="opacity: 0.75;">${formatPercent(share)} der Kosten</div>`}`;
    };

    private _monthlyChartSelected(e: CustomEvent<ChartPointEventDetail>) {
        const label = e.detail.point?.xValue;
        const month = this._monthlyReports.findIndex((r) => this._monthLabel(r) === label);
        if (month !== -1) {
            this.go(null, { month, year: this._year });
        }
    }

    private _updateSavingsReturn() {
        const savings = this._dailyReports.reduce((total, report) => total + report.savings, 0);
        const fees = this._dailyReports.reduce((total, report) => total + report.fees, 0);

        this._monthTotals = { savings, fees };
        this._chartSavingsReturn = returnChart(this._monthTotals);
    }

    private _updateDailyCharts() {
        this._chartSavingsDaily = savingsChart(savingsDays(this._dailyReports), this._monthTotals.fees, {
            yAxis: "right",
            legend: false,
            highlight: this._highlight,
        });
    }

    private _returnLabel() {
        const { savings, fees } = this._monthTotals;
        return savings >= fees
            ? `Ringdiagramm: ${formatCurrency(savings)} Ersparnis, davon ${formatCurrency(fees)} Gebühren und ${formatCurrency(savings - fees)} Netto-Ersparnis.`
            : `${formatCurrency(fees)} Gebühren stehen bisher nur ${formatCurrency(savings)} Ersparnis gegenüber.`;
    }

    private _renderReturn() {
        return html`
            <ptc-ui-chart
                class="chart-savings-return"
                label=${this._returnLabel()}
                .height=${200}
                .definition=${this._chartSavingsReturn}
            ></ptc-ui-chart>
        `;
    }

    private _legendItems() {
        const { savings, fees } = this._monthTotals;
        return [
            { label: "Ersparnis", color: savingsColors.Ersparnis!, value: formatCurrency(savings) },
            { label: "Gebühren", color: savingsColors.Gebühren!, value: formatCurrency(fees) },
        ];
    }

    static styles = [
        shared,
        css`
            :host {
                display: grid;
                grid-template-columns: 200px 1fr;
            }

            .header {
                border-bottom: solid 1px var(--shade-2);
            }

            .main {
                position: relative;
            }

            .scroller {
                overflow: auto;
            }

            .charts {
                position: sticky;
                left: 0;
                padding: 0 1em;
                display: flex;
                align-items: center;
            }

            .charts-savings-daily {
                flex: 1;
                width: 0;
            }

            .charts-savings-monthly {
                flex: 1;
                width: 0;
            }

            .charts-savings-return,
            .charts-savings-total {
                width: 265px;
                display: flex;
                flex-direction: column;
                justify-content: center;
            }

            .charts-title {
                font-size: var(--font-size-large);
                padding: 0.5em;
                font-weight: 600;
                text-align: center;
            }

            .charts-savings {
                margin-top: 1em;
            }

            .charts-savings-return {
                gap: 0.75em;
            }

            .chart-savings-return {
                display: block;
            }

            .chart-savings-total-euro,
            .chart-savings-total-percent {
                text-align: center;
                font-size: var(--font-size-enormous);
                color: var(--color-highlight);
                font-weight: 300;
            }
        `,
    ];

    render() {
        if (!app.company) {
            return html``;
        }

        const years: number[] = [];
        for (let year = new Date().getFullYear(); year > 2010; year--) {
            years.push(year);
        }

        const costTotal = this._monthlyReports.reduce((total, s) => total + s.actualCosts, 0);
        const savingsTotal = this._monthlyReports.reduce((total, s) => total + s.savings, 0);
        const savingsPercent = costTotal ? (savingsTotal / costTotal) * 100 : 0;

        return html`
            <div class="fullbleed vertical layout">
                <div class="horizontal padded spacing center-aligning layout border-bottom">
                    <div class="stretch"></div>
                    <ptc-ui-icon name="icon.status.info" id="savingsInfo"></ptc-ui-icon>
                    <ptc-ui-tooltip for="savingsInfo" style="--tooltip-max-width: 25em; --tooltip-text-align: left">
                        Die hier gezeigten Ersparnisse ergeben sich aus den
                        <strong>Regeln, die Sie für die Zeiterfassung definiert haben</strong>. Es werden je nach
                        Einstellung <strong>zu früher Dienstbeginn</strong>, <strong>Verspätung</strong>,
                        <strong>Minutenrundung</strong> und <strong>Raucherpausen</strong> Mitarbeiter-genau erhoben und
                        mit dessen Lohndaten berechnet.
                        <strong
                            >Dieser Bericht setzt die Nutzung der elektronischen Zeiterfassung von Pentacode
                            voraus.</strong
                        >
                    </ptc-ui-tooltip>
                    <select id="monthSelect" @change=${this._monthSelected} class="slim">
                        ${monthNames.map((month, i) => html` <option .value=${i.toString()}>${month}</option> `)}
                    </select>
                    <select id="yearSelect" @change=${this._yearSelected} class="slim">
                        ${years.map((year) => html` <option .value=${year.toString()}>${year}</option> `)}
                    </select>
                </div>

                <div class="scroller stretch" ?hidden=${!savingsTotal}>
                    <div class="charts charts-savings">
                        <div class="charts-savings-return">
                            <div class="charts-title">${monthNames[this._month]} ${this._year}</div>
                            ${this._renderReturn()}
                            <ptc-ui-chart-legend
                                .items=${this._legendItems()}
                                .highlight=${this._highlight}
                                @chart-legend-highlight=${(e: CustomEvent<{ label: string | null }>) =>
                                    (this._highlight = e.detail.label ?? undefined)}
                            ></ptc-ui-chart-legend>
                        </div>
                        <div class="charts-savings-daily">
                            <ptc-ui-chart
                                class="chart-savings-daily"
                                label="Ersparnis: kumulierte Einsparung pro Tag im gewählten Monat"
                                .height=${300}
                                .definition=${this._chartSavingsDaily}
                            ></ptc-ui-chart>
                        </div>
                    </div>

                    <div class="charts">
                        <div class="charts-savings-total vertical center-justifying layout">
                            <div class="charts-title">${this._year} Gesamt</div>
                            <div class="chart-savings-total-euro blue">${formatNumber(savingsTotal)} €</div>
                            <div class="chart-savings-total-percent green">${fmtNum(savingsPercent, 1)} %</div>
                        </div>

                        <div class="charts-savings-monthly">
                            <ptc-ui-chart
                                class="chart-savings-monthly"
                                label="Monatliche Ersparnis in Euro als Balken und Gebühr als Linie"
                                .height=${300}
                                .tooltip=${this._renderMonthlyTooltip}
                                .definition=${this._chartSavingsMonthly}
                                @chart-select=${this._monthlyChartSelected}
                            ></ptc-ui-chart>
                        </div>
                    </div>
                </div>

                <div class="stretch centering layout" ?hidden=${this._loading || !!savingsTotal}>
                    <div class="margined double-padded box">
                        <ptc-ui-icon name="icon.status.info"></ptc-ui-icon>
                        Der Ersparnisbericht erfordert die Nutzung der elektronischen Zeiterfassung.
                    </div>
                </div>

                <div
                    class="fullbleed center-aligning center-justifying vertical layout scrim"
                    ?hidden=${!this._loading}
                >
                    <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
                </div>
            </div>
        `;
    }
}
